import { Image } from "@/components/ui/image";
import { cn } from "@/lib/utils";
import { HIDDEN_BESIDE_HERO_ASK, SHOWN_BESIDE_HERO_ASK } from "@/components/layout/hero-ask-visibility";
import {
  ARROWHEAD_PATHS,
  ARROWHEAD_STROKE_WIDTH,
  LOGO_MARK_VIEW_BOX,
  LOGO_VIEW_BOX,
  LOOP_PATHS,
  LOOP_STROKE_WIDTH,
  WORDMARK_BRAND_PATH,
  WORDMARK_INK_PATH,
} from "@shared/design-system/logo-mark";

function ArrowLoop() {
  return (
    <g className="fill-brand stroke-brand">
      {LOOP_PATHS.map((path) => (
        <path key={path} d={path} fill="none" strokeWidth={LOOP_STROKE_WIDTH} strokeMiterlimit={10} />
      ))}
      {ARROWHEAD_PATHS.map((path) => (
        <path key={path} d={path} strokeWidth={ARROWHEAD_STROKE_WIDTH} />
      ))}
    </g>
  );
}

function accessibleName(alt: string) {
  return alt === "" ? { "aria-hidden": true as const } : { role: "img" as const, "aria-label": alt };
}

type LogoProps = {
  className?: string;
  alt?: string;
  inkClassName?: string;
};

export function RepackifyLogo({ className, alt = "Repackify", inkClassName = "fill-foreground" }: LogoProps) {
  return (
    <svg
      viewBox={LOGO_VIEW_BOX}
      className={cn("h-7 w-auto shrink-0", className)}
      {...accessibleName(alt)}
    >
      <g transform="translate(0 2)">
        <ArrowLoop />
      </g>
      <path d={WORDMARK_INK_PATH} className={inkClassName} />
      <path d={WORDMARK_BRAND_PATH} className="fill-brand" />
    </svg>
  );
}

export function RepackifyMark({ className, alt = "Repackify" }: LogoProps) {
  return (
    <svg
      viewBox={LOGO_MARK_VIEW_BOX}
      className={cn("h-6 w-auto shrink-0", className)}
      {...accessibleName(alt)}
    >
      <ArrowLoop />
    </svg>
  );
}

export function RepackifyBrand({
  size = "header",
  compactOnMobile = false,
  compactBesideAskPanel = false,
  inkClassName,
}: {
  size?: "compact" | "header";
  compactOnMobile?: boolean;
  compactBesideAskPanel?: boolean;
  inkClassName?: string;
}) {
  const markOnMobile = compactOnMobile || size === "compact";
  const wordmarkBesideHeroAsk = compactOnMobile && cn("max-sm:h-5", SHOWN_BESIDE_HERO_ASK);
  const askPanelCompact = compactBesideAskPanel && "[:root[data-ask-panel=open]_&]:xl:max-2xl:hidden";
  return (
    <span className="inline-flex shrink-0 items-center gap-2">
      {(markOnMobile || compactBesideAskPanel) && (
        <RepackifyMark
          className={cn(
            "h-5",
            markOnMobile ? "sm:hidden" : "hidden",
            compactOnMobile && HIDDEN_BESIDE_HERO_ASK,
            compactBesideAskPanel && "[:root[data-ask-panel=open]_&]:xl:max-2xl:block",
          )}
        />
      )}
      <RepackifyLogo
        className={cn(size === "compact" ? "h-5" : "h-6.5", markOnMobile && "hidden sm:block", wordmarkBesideHeroAsk, askPanelCompact)}
        inkClassName={inkClassName}
      />
      <Image
        src="/brand/seasonal/halloween.svg"
        alt=""
        width={100}
        height={123}
        unoptimized
        loading="lazy"
        className={cn(
          "hidden w-auto shrink-0 in-data-[season=halloween]:block",
          size === "compact" ? "h-6" : "h-8",
          compactOnMobile && "max-sm:hidden",
          askPanelCompact,
        )}
      />
    </span>
  );
}
